import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { InternationalCallsReportParameters } from '@ndt/report-output/international-calls/international-calls';
import { BooleanReportParameter } from '@ndt/report-output/shared/models/boolean-report-parameter';
import { ReportCallDirections } from '@ndt/report-output/shared/models/call-direction-report-parameter';
import { defaultAllMultiEntitySelectParameter } from '@ndt/report-output/shared/models/multi-entity-selection-report-parameter';
import { TimeZoneReportParameter } from '@ndt/report-output/shared/models/time-zone-report-parameters';
import { createDateRangeReportParameter } from '@ndt/reports/shared/models/date-range-report-parameter-value';
import { defaultTimeZoneParameter } from '@ndt/reports/shared/models/time-zone-parameter-value';
import {
   convertFromFormValueList,
   ParameterFormValueUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-form-value';
import {
   CallDirectionsToIncludeReportParameter,
   convertToDateRangeReportParameter,
   convertToForm,
   EntitiesToIncludeParameter,
   ReportParametersUnion,
} from '@ndt/reports/shared/report-parameter-list/models/report-parameter-union.model';
import { ReportParameterConfig } from '@ndt/reports/shared/report-parameter-list/report-parameter.config';
import { Reports } from '@ndt/reports/shared/reports.enum';
import { Store } from '@ngrx/store';
import { ReportParameters } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { LcsDateTimeAdapter } from 'src/app/common-components/date-time-picker/configuration/date-time-adapter';
import { throwIfNullOrUndefined } from 'src/app/common-components/utils/type-safety';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import {
   ReportParameterFormService,
   ReportParameterFormServiceBase,
} from '../../shared/report-parameter-list/services/report-parameter-form.service';

export class InternationalCallsParametersFormService
   extends ReportParameterFormServiceBase
   implements ReportParameterFormService<InternationalCallsReportParameters>
{
   config: ReportParameterConfig[];

   form: UntypedFormGroup;
   selectedAccountId$: Observable<number | null>;

   constructor(fb: UntypedFormBuilder, private dateAdapter: LcsDateTimeAdapter<Date>, store: Store) {
      super(fb);
      const initialValue: ReportParametersUnion[] = [
         {
            parameterType: ReportParameters.DateRange,
            ...createDateRangeReportParameter(),
            sortOrder: 1,
         },
         {
            parameterType: ReportParameters.CallDirectionsToInclude,
            sortOrder: 2,
            value: ReportCallDirections.Both,
         },
         {
            parameterType: ReportParameters.GroupByExtension,
            sortOrder: 3,
            value: false,
         },
         {
            parameterType: ReportParameters.TimeZone,
            sortOrder: 4,
            value: '',
         },
         {
            parameterType: ReportParameters.ExtensionsToInclude,
            accountId: 0,
            all: true,
            names: [],
            sortOrder: 5,
            values: [],
            hidden: true,
         },
      ];
      this.selectedAccountId$ = store.select(getSelectedAccountId);

      this.config = initialValue.map((ap) => {
         return this.getReportParameterConfig(ap.parameterType, undefined, false, Reports.InternationalCalls);
      });
      this.form = convertToForm(initialValue, fb);
   }

   patchSavedValues(values: ReportParametersUnion[], showAskToggle: boolean, showAskParameters: boolean): void {
      throw new Error('Method not implemented.');
   }

   resetConfig(): void {
      throw new Error('Method not implemented.');
   }

   // getLayoutConfigurations(values: ReportParametersUnion[]): {
   //    name: string | null;
   //    color: string | null;
   //    refreshTimeSeconds: number | null;
   // } {
   //    throw new Error('Method not implemented.');
   // }

   setValue(values: ParameterFormValueUnion[]): void {
      this.form.setValue(values, { emitEvent: false });
   }

   getValue(includeAskParameters?: boolean | undefined): InternationalCallsReportParameters | null {
      const formValue = this.form.getRawValue();
      const values: ParameterFormValueUnion[] = formValue.values;
      const convertedValues = convertFromFormValueList(values);
      return this.convertValue(convertedValues);
   }

   convertValue(values: ReportParametersUnion[]): InternationalCallsReportParameters | null {
      const extensions = values.find((v) => {
         return v.parameterType === ReportParameters.ExtensionsToInclude;
      }) as EntitiesToIncludeParameter | undefined;

      const callDirectionsToInclude = values.find((v) => {
         return v.parameterType === ReportParameters.CallDirectionsToInclude;
      }) as CallDirectionsToIncludeReportParameter | undefined;

      const groupByExtension = values.find((v) => {
         return v.parameterType === ReportParameters.GroupByExtension;
      }) as BooleanReportParameter | undefined;

      const timeZone = values.find((v) => {
         return v.parameterType === ReportParameters.TimeZone;
      }) as TimeZoneReportParameter | undefined;

      const output: InternationalCallsReportParameters = {
         callDirection: callDirectionsToInclude
            ? {
                 value: callDirectionsToInclude.value,
              }
            : {
                 value: ReportCallDirections.Both,
              },
         dateRange: throwIfNullOrUndefined(convertToDateRangeReportParameter(values, this.dateAdapter)),
         extensions: extensions
            ? {
                 accountId: extensions.accountId,
                 all: !!extensions?.all,
                 values: extensions.values || [],
              }
            : defaultAllMultiEntitySelectParameter,
         groupByExtension: groupByExtension
            ? {
                 value: groupByExtension.value,
              }
            : {
                 value: false,
              },
         timeZone: timeZone
            ? {
                 value: timeZone.value,
              }
            : defaultTimeZoneParameter,
      };
      this.selectedAccountId$.subscribe((accountId) => {
         output.extensions.accountId = accountId;
      });

      return output;
   }

   isListComplete(values: ReportParametersUnion[], includeAskParameters = false): boolean {
      const validParameters = !includeAskParameters
         ? values.filter((v) => {
              return !false;
           })
         : values;

      const dateRange = validParameters.find((v) => {
         return v.parameterType === ReportParameters.DateRange;
      });

      return !!dateRange;
   }
}
